/**
 * @file Error handling utilities for standardized error handling across the application
 */

import { toast } from 'sonner';
import { debug } from './utils';

/**
 * Enumeration of possible error types for better categorization
 */
export enum ErrorType {
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  NOT_FOUND = 'not_found',
  VALIDATION = 'validation',
  SERVER = 'server',
  NETWORK = 'network',
  UNKNOWN = 'unknown',

  // Quote-specific error types
  QUOTE_NOT_FOUND = 'quote_not_found',
  QUOTE_ALREADY_PROCESSED = 'quote_already_processed',
  QUOTE_EXPIRED = 'quote_expired',
  QUOTE_INVALID_TRANSITION = 'quote_invalid_transition'
}

/**
 * Standardized application error interface
 */
export interface AppError {
  type: ErrorType;
  message: string;
  originalError?: unknown;
}

/**
 * Creates a standardized AppError from various error types
 * @param error The original error object
 * @returns A standardized AppError
 */
export function createAppError(error: unknown): AppError {
  // Check if it's already an AppError
  if (error && typeof error === 'object' && 'type' in error && 'message' in error) {
    return error as AppError;
  }

  // Check for Supabase authentication errors
  if (error?.message?.includes('auth')) {
    return {
      type: ErrorType.AUTHENTICATION,
      message: error.message || 'Authentication error',
      originalError: error
    };
  }

  // Check for 401/403 errors (Supabase RLS or auth issues)
  if (error?.status === 401 || error?.status === 403 || error?.code === 'PGRST301') {
    return {
      type: ErrorType.AUTHORIZATION,
      message: 'You do not have permission to perform this action',
      originalError: error
    };
  }

  // Check for 404 errors
  if (error?.status === 404 || error?.code === 'PGRST116') {
    return {
      type: ErrorType.NOT_FOUND,
      message: 'The requested resource was not found',
      originalError: error
    };
  }

  // Check for network errors
  if (error?.message?.includes('network') || error?.message?.includes('Failed to fetch')) {
    return {
      type: ErrorType.NETWORK,
      message: 'Network error. Please check your connection',
      originalError: error
    };
  }

  // Check for validation errors (form validation, etc.)
  if (error?.message?.includes('valid') || error?.code === 'PGRST109') {
    return {
      type: ErrorType.VALIDATION,
      message: error.message || 'Validation error',
      originalError: error
    };
  }

  // Server errors (500, etc.)
  if (error?.status >= 500 || error?.code?.startsWith('PGRST5')) {
    return {
      type: ErrorType.SERVER,
      message: 'Server error. Please try again later',
      originalError: error
    };
  }

  // Check for quote-specific errors
  if (error?.message?.includes('quote not found') ||
      error?.message?.includes('quote does not exist')) {
    return {
      type: ErrorType.QUOTE_NOT_FOUND,
      message: 'Quote not found',
      originalError: error
    };
  }

  if (error?.message?.includes('already processed') ||
      error?.message?.includes('quote already') ||
      error?.message?.includes('has been priced')) {
    return {
      type: ErrorType.QUOTE_ALREADY_PROCESSED,
      message: 'This quote has already been processed',
      originalError: error
    };
  }

  if (error?.message?.includes('expired') ||
      error?.message?.includes('past due date')) {
    return {
      type: ErrorType.QUOTE_EXPIRED,
      message: 'This quote has expired',
      originalError: error
    };
  }

  if (error?.message?.includes('invalid transition') ||
      error?.message?.includes('cannot change from') ||
      error?.message?.includes('phase cannot be changed')) {
    return {
      type: ErrorType.QUOTE_INVALID_TRANSITION,
      message: 'Invalid quote status change',
      originalError: error
    };
  }

  // Default to unknown error
  return {
    type: ErrorType.UNKNOWN,
    message: error?.message || 'An unexpected error occurred',
    originalError: error
  };
}

/**
 * Get a user-friendly error message based on error type
 * @param error The standardized AppError
 * @returns A user-friendly error message
 */
function getErrorMessage(error: AppError): string {
  switch (error.type) {
    case ErrorType.AUTHENTICATION:
      return 'Authentication error. Please log in again.';
    case ErrorType.AUTHORIZATION:
      return 'You do not have permission to perform this action.';
    case ErrorType.NOT_FOUND:
      return 'The requested resource was not found.';
    case ErrorType.VALIDATION:
      return error.message || 'Please check your input and try again.';
    case ErrorType.SERVER:
      return 'Server error. Please try again later.';
    case ErrorType.NETWORK:
      return 'Network error. Please check your connection.';

    // Quote-specific error messages
    case ErrorType.QUOTE_NOT_FOUND:
      return 'The quote you are looking for could not be found.';
    case ErrorType.QUOTE_ALREADY_PROCESSED:
      return 'This quote has already been processed.';
    case ErrorType.QUOTE_EXPIRED:
      return 'This quote has expired. Please request a new one.';
    case ErrorType.QUOTE_INVALID_TRANSITION:
      return 'Cannot change the quote to this status.';

    case ErrorType.UNKNOWN:
    default:
      return error.message || 'An unexpected error occurred.';
  }
}

/**
 * Standardized error handler for API calls
 * @param error The original error object
 * @param userMessage Optional custom message to show to the user
 * @returns The standardized AppError for further handling
 */
export function handleApiError(error: unknown, userMessage?: string): AppError {
  const appError = createAppError(error);
  debug.error('API error:', appError);

  const message = userMessage || getErrorMessage(appError);
  toast.error(message);

  return appError;
}

/**
 * Attempts to execute a function and handle any errors in a standardized way
 * @param fn The function to execute
 * @param userMessage Optional custom message to show on error
 * @returns A promise that resolves to the function result or rejects with an AppError
 */
export async function tryAsync<T>(
  fn: () => Promise<T>,
  userMessage?: string
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    handleApiError(error, userMessage);
    throw error;
  }
}

/**
 * Higher-order function that wraps an API function with standardized error handling
 * @param fn The API function to wrap
 * @param fallbackValue The value to return if the function fails
 * @param userMessage Optional custom message to show on error
 * @returns A function that executes the original function with error handling
 */
export function withErrorHandling<T, Args extends unknown[]>(
  fn: (...args: Args) => Promise<T>,
  fallbackValue: T,
  userMessage?: string
): (...args: Args) => Promise<T> {
  return async (...args: Args): Promise<T> => {
    try {
      return await fn(...args);
    } catch (error) {
      handleApiError(error, userMessage);
      return fallbackValue;
    }
  };
}
